<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>エクストラネット接続一覧</span>
        <manual-button path="/nwco-extra-list/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search :search="extranet.search" v-model="extranet.filter" />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn
              v-permission.config
              color="primary"
              @click="showExtraConnectReg"
              >接続申請</v-btn
            >
            <v-btn :href="downloadUrl" download color="primary"
              >一覧ダウンロード</v-btn
            >
          </div>
        </v-row>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="extranet.sortBy"
          :fields="extranet.fields"
          :items="extranet.items"
          :filter="extranet.filter"
          :filterOption="extranet.filter_option"
          :hover="true"
          :sort-compare="defaultSort"
          :sort-compare-key="['srcVNumber']"
          primary-key="extranetSeq"
          :sort-null-last="true"
          @click:row="onRowSelected"
          sticky-header="50vh"
        >
          <!-- 接続元V番号 -->
          <template #cell(srcVNumber)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
          <!-- 接続先V番号 -->
          <template #cell(dstVNumber)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
          <!-- PINコード -->
          <template #cell(pinCode)="data">
            {{ $filter.emptyTo(data.value, "-") }}
          </template>

          <!-- 申請日時 -->
          <template #cell(requestDateTime)="data">
            {{ $filter.datetime(data.value) }}
          </template>

          <!-- 有効期限 -->
          <template #cell(expiredDateTime)="data">
            {{ $filter.emptyTo($filter.datetime(data.value), "-") }}
          </template>

          <!-- 承認日時 -->
          <template #cell(approvalDateTime)="data">
            {{ $filter.emptyTo($filter.datetime(data.value), "-") }}
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { ExtranetInfo } from "@/apis/ExtranetApi";
import type { SearchOption } from "@/components/AppSearch.vue";
import type {
  SortItem,
  AppTableHeader,
  FilterOption,
} from "@/components/AppTable.vue";
import ExtranetConnectReference from "@/modals/extranet/ExtranetConnectReference.vue";
import ExtranetConnectRegister from "@/modals/extranet/ExtranetConnectRegister.vue";

export default defineComponent({
  name: "ExtranetConnectList",
  data() {
    return {
      extranet: {
        sortBy: { key: "srcVNumber", order: "desc" } as SortItem,
        fields: [
          {
            key: "srcVNumber",
            title: "接続元V番号",
            sortable: true,
            width: "110px",
          },
          {
            key: "srcVpnVnCode",
            title: "接続元VPN/VNコード",
            sortable: true,
            width: "120px",
          },
          {
            key: "dstVNumber",
            title: "接続先V番号",
            sortable: true,
            width: "110px",
          },
          {
            key: "dstVpnVnCode",
            title: "接続先VPN/VNコード",
            sortable: true,
            width: "120px",
          },
          {
            key: "pinCode",
            title: "PINコード",
            width: "83px",
          },
          {
            key: "approvalState",
            title: "承認ステータス",
            sortable: true,
            width: "120px",
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (
              val: ExtranetInfo["approvalState"],
              _k,
              item: ExtranetInfo
            ) => {
              // 承認側(接続元V番号とユーザーのV番号が異なる)で申請中の場合は未承認、それ以外は通常の区分値を表示
              if (
                this.$store.state.user.contractSummary.vnumber !==
                  item.srcVNumber &&
                val === "APPLIED"
              ) {
                return "未承認";
              } else {
                return this.$filter.enumTo(val, "approvalState");
              }
            },
          },
          {
            key: "requestDateTime",
            title: "申請日時",
            width: "125px",
          },
          {
            key: "expiredDateTime",
            title: "有効期限",
            width: "125px",
          },
          {
            key: "approvalDateTime",
            title: "承認日時",
            width: "125px",
          },
        ] as AppTableHeader<ExtranetInfo>[],
        items: [] as ExtranetInfo[],
        search: {
          srcVNumber: {
            type: "text",
            label: "接続元V番号",
          },
          srcVpnVnCode: {
            type: "text",
            label: "接続元VPN/VNコード",
          },
          dstVNumber: {
            type: "text",
            label: "接続先V番号",
          },
          dstVpnVnCode: {
            type: "text",
            label: "接続先VPN/VNコード",
          },
          approvalState: {
            type: "select",
            label: "承認ステータス",
            items: [
              { value: "完了", text: "完了" },
              { value: "申請中", text: "申請中" },
              { value: "未承認", text: "未承認" },
            ],
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          srcVNumber: "like",
          srcVpnVnCode: "like",
          dstVNumber: "like",
          dstVpnVnCode: "like",
          approvalState: "equal",
        } as FilterOption,
      },
      extranetKeyId: "",
      isLoaded: false,
      downloadUrl: this.$api.extranetApi.getCsvExtranetURL(),
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    /** データ取得 */
    async load() {
      // エクストラネット接続一覧取得APIを呼び出す
      const res = await this.$api.extranetApi.getExtranets();
      this.extranet.items = res.extranetInfo;
      this.extranetKeyId = res.extranetKeyId;

      // 一覧データの絞り込み
      this.filterItems();
    },

    /** 承認ステータスが未承認、完了のデータのみ抽出する処理 */
    filterItems() {
      this.extranet.items = this.extranet.items.filter((item) => {
        return (
          item.approvalState === "COMPLETE" || item.approvalState === "APPLIED"
        );
      });
    },

    /**
     * エクストラネットのリストの初期ソート処理
     * ソート条件：接続元V番号の降順、接続元VPNコード降順、接続元VNコードの降順
     * テーブルの方でsort-descをtrueにしているので、このソートの結果は反転するようになっています
     * そのため、コメント内容とif分岐の内容が反対になっています
     */
    defaultSort(a: ExtranetInfo, b: ExtranetInfo): number {
      // 接続元V番号だけカスタムソート それ以外は通常のソートを行う
      // デフォルトの状態でnullが最後に来るようにする
      if (a.srcVNumber === null && b.srcVNumber !== null) {
        return -1;
      } else if (a.srcVNumber !== null && b.srcVNumber === null) {
        return 1;
      } else if (a.srcVNumber !== null && b.srcVNumber !== null) {
        // 接続元V番号の降順にソート
        if (a.srcVNumber !== b.srcVNumber) {
          if (a.srcVNumber < b.srcVNumber) {
            return -1;
          } else {
            return 1;
          }
        }
      }

      // 接続元VPN/VNコードをVPNコード、VNコードの順にソート
      // NOTE：VPNコードは接頭辞がB、VNコードは接頭辞がCまたはD
      const aPrefix = a.srcVpnVnCode.charAt(0);
      const bPrefix = b.srcVpnVnCode.charAt(0);
      if (aPrefix !== bPrefix) {
        // VPNが先、VNが後になるようにソート
        if (aPrefix.startsWith("B")) {
          return 1;
        } else if (bPrefix.startsWith("B")) {
          return -1;
        } else {
          if (aPrefix < bPrefix) {
            return -1;
          } else {
            return 1;
          }
        }
      }

      // VPN/VN順のソートは済んでいるので、接尾辞を抜き出し降順にソート
      const aSuffix = a.srcVpnVnCode.substr(1);
      const bSuffix = b.srcVpnVnCode.substr(1);
      if (aSuffix !== bSuffix) {
        if (aSuffix < bSuffix) {
          return -1;
        } else {
          return 1;
        }
      }

      // 接続先VPN/VNコードの降順にソート
      if (a.dstVpnVnCode !== b.dstVpnVnCode) {
        if (a.dstVpnVnCode < b.dstVpnVnCode) {
          return -1;
        } else {
          return 1;
        }
      }
      return 0;
    },

    /** エクストラネット接続申請を表示する */
    async showExtraConnectReg() {
      await this.$modal.show(ExtranetConnectRegister, {
        extranetKeyId: this.extranetKeyId,
      });
      await this.load();
    },

    /**
     * エクストラネット参照モーダルを表示する
     * @param item 押下した行のデータ
     */
    async onRowSelected(item: ExtranetInfo) {
      await this.$modal.show(ExtranetConnectReference, {
        connectInfo: item,
        extranetKeyId: this.extranetKeyId,
      });
      await this.load();
    },
  },
});
</script>
